import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  XCircle
} from 'lucide-react';
import { useState } from 'react';

const TestCaseSummary = ({
  testCaseSummary,
  testCaseResults
}: {
  testCaseSummary: any;
  testCaseResults: any[];
}) => {
  const [showTestCases, setShowTestCases] = useState(true);
  return (
    <div className='mb-4 rounded-lg bg-white p-4 shadow-lg sm:mb-6 sm:p-6'>
      <div className='mb-4 flex items-center justify-between'>
        <div className='flex items-center space-x-3'>
          <FlaskConical className='h-5 w-5 text-indigo-600 sm:h-6 sm:w-6' />
          <h2 className='text-lg font-semibold text-gray-900 sm:text-xl'>
            Test Case Summary
          </h2>
        </div>
        <button
          onClick={() => setShowTestCases(v => !v)}
          className='flex items-center space-x-1 rounded-md bg-gray-100 px-3 py-1.5 text-gray-600 transition-colors hover:bg-gray-200'
        >
          {showTestCases ? (
            <ChevronUp className='h-4 w-4' />
          ) : (
            <ChevronDown className='h-4 w-4' />
          )}
          <span className='text-sm'>
            {showTestCases ? 'Collapse' : 'Expand'}
          </span>
        </button>
      </div>

      <div className='mb-4 grid grid-cols-3 gap-3 sm:gap-4'>
        <div className='rounded-lg bg-gray-50 p-3 text-center'>
          <p className='text-2xl font-bold text-gray-800 sm:text-3xl'>
            {testCaseSummary.totalTestCases}
          </p>
          <p className='mt-0.5 text-xs text-gray-500 sm:text-sm'>Total</p>
        </div>
        <div className='rounded-lg bg-green-50 p-3 text-center'>
          <p className='text-2xl font-bold text-green-700 sm:text-3xl'>
            {testCaseSummary.passedTestCases}
          </p>
          <p className='mt-0.5 text-xs text-green-600 sm:text-sm'>Passed</p>
        </div>
        <div className='rounded-lg bg-red-50 p-3 text-center'>
          <p className='text-2xl font-bold text-red-600 sm:text-3xl'>
            {testCaseSummary.failedTestCases}
          </p>
          <p className='mt-0.5 text-xs text-red-500 sm:text-sm'>Failed</p>
        </div>
      </div>

      {showTestCases && testCaseResults.length > 0 && (
        <div className='mt-5 space-y-3'>
          <h3 className='text-sm font-semibold text-gray-700'>
            Individual Test Cases
          </h3>
          {testCaseResults?.map((tc: any, idx: number) => (
            <div
              key={tc}
              className={`overflow-hidden rounded-lg border ${
                tc.status === 'PASS'
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }`}
            >
              <div
                className={`flex items-center justify-between px-4 py-2 ${
                  tc.status === 'PASS' ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                <span className='text-sm font-medium text-gray-700'>
                  Test Case {idx + 1}
                </span>
                <span
                  className={`flex items-center space-x-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    tc.status === 'PASS'
                      ? 'bg-green-200 text-green-800'
                      : 'bg-red-200 text-red-800'
                  }`}
                >
                  {tc.status === 'PASS' ? (
                    <CheckCircle className='mr-1 h-3 w-3' />
                  ) : (
                    <XCircle className='mr-1 h-3 w-3' />
                  )}
                  {tc.status}
                </span>
              </div>
              <div className='grid grid-cols-1 gap-2 p-3 sm:grid-cols-3 sm:gap-3'>
                <div>
                  <p className='mb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
                    Input
                  </p>
                  <code className='block rounded bg-white px-2 py-1.5 text-xs text-gray-800 shadow-sm'>
                    {tc.testCaseInput || '—'}
                  </code>
                </div>
                <div>
                  <p className='mb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
                    Expected Output
                  </p>
                  <code className='block rounded bg-white px-2 py-1.5 text-xs text-gray-800 shadow-sm'>
                    {tc.expectedOutput || '—'}
                  </code>
                </div>
                <div>
                  <p className='mb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
                    Actual Output
                  </p>
                  <code
                    className={`block rounded px-2 py-1.5 text-xs shadow-sm ${
                      tc.status === 'PASS'
                        ? 'bg-white text-gray-800'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {tc.actualOutput !== undefined && tc.actualOutput !== ''
                      ? tc.actualOutput
                      : '(no output)'}
                  </code>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestCaseSummary;
